import { logToServer } from '@/services/LogService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { doLogout, selectCredentials } from '@/state/reducers/authSlice';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logger } from '../logger';
import useToast from './useToast';

const DEFAULT_EVENTS: (keyof DocumentEventMap)[] = ['keypress', 'mousemove', 'touchmove', 'click', 'scroll'];
const DEFAULT_OPTIONS = {
    events: DEFAULT_EVENTS,
    initialState: false
};

const expirationDuration = +(window.config.sessionExpirationTimer || 60000);

export function useIdleAbsolute(timeout: number) {
    const [toastError, toastSuccess] = useToast();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const credentials = useAppSelector(selectCredentials);

    const { events, initialState } = { ...DEFAULT_OPTIONS };
    const [idle, setIdle] = useState<boolean>(initialState);
    const lastActivityTimeRef = useRef<Date>(new Date());

    const resetIdleTimer = () => {
        const now = new Date().getTime();
        const lastActivity = lastActivityTimeRef.current.getTime();
        const idleTime = now - lastActivity;  
        if (idleTime >= timeout + expirationDuration) {
            onLogout();
        } else {
            lastActivityTimeRef.current = new Date();
        }
    };

    const onLogout = async (forced = false) => {
        Logger({ title: `Idle absolute initiated logout.`, callback: () => {} });
        try {
            if (credentials) await dispatch(doLogout({ credentials }) as any).unwrap();
        } catch (rejectedValueOrSerializedError) {
            console.warn('Could not actively logout, due to timed out session token, clearing persisted credentials');
        }
        navigate('/login');
        if (forced) {
            // handle error here
            toastError({ body: 'Logged out due to inactivity', title: 'Service disconnected' });
        }
        logToServer('info', `User [${credentials?.username}] has been logged out due to inactivity (absolute).`);
    };

    useEffect(() => {
        const activityHandler = () => resetIdleTimer();

        // Attach listeners
        events.forEach((event) => window.addEventListener(event, activityHandler));

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const lastActivity = lastActivityTimeRef.current.getTime();
            const idleTime = now - lastActivity;

            if (idleTime >= timeout + expirationDuration) {
                onLogout();
            }
        }, 1000); // check every second

        return () => {
            // Cleanup
            events.forEach((event) => window.removeEventListener(event, activityHandler));
            clearInterval(interval);
        };
    }, [timeout, onLogout]);

    return idle;
}
